<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import OpenHoursTimeline from './OpenHoursTimeline.vue';
import { timeZoneOptions } from '@/dashboard/routes/dashboard/settings/inbox/helpers/businessHour';

const props = defineProps({
  schedule: { type: Object, default: () => null },
  timezone: { type: String, default: '' },
  active: { type: Boolean, default: true },
  saving: { type: Boolean, default: false },
  workingHoursEnabled: { type: Boolean, default: false },
});

const emit = defineEmits(['updateSchedule', 'updateAvailability']);

const ALL_DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const DAY_I18N_KEYS = {
  monday: 'SETTINGS.SPACES.SCHEDULE.MONDAY',
  tuesday: 'SETTINGS.SPACES.SCHEDULE.TUESDAY',
  wednesday: 'SETTINGS.SPACES.SCHEDULE.WEDNESDAY',
  thursday: 'SETTINGS.SPACES.SCHEDULE.THURSDAY',
  friday: 'SETTINGS.SPACES.SCHEDULE.FRIDAY',
  saturday: 'SETTINGS.SPACES.SCHEDULE.SATURDAY',
  sunday: 'SETTINGS.SPACES.SCHEDULE.SUNDAY',
};

const HOUR_WIDTH_PX = 80;
const TRACK_WIDTH_PX = 24 * HOUR_WIDTH_PX;
const HOUR_LABELS = Array.from({ length: 24 }, (_, hour) =>
  String(hour).padStart(2, '0')
);

const SNAP_MINUTES = 15;
const MIN_DURATION = 15;
const DEFAULT_SLOT_DURATION = 60;
const DAY_MINUTES = 24 * 60 - 1;
const PREFERRED_SLOT_START = 9 * 60;

const parseHm = hm => {
  const [h, m] = String(hm || '')
    .split(':')
    .map(n => parseInt(n, 10));
  if (!Number.isFinite(h)) return null;
  return h * 60 + (Number.isFinite(m) ? m : 0);
};

const formatHm = totalMinutes => {
  const clamped = Math.max(0, Math.min(DAY_MINUTES, Math.round(totalMinutes)));
  const hours = Math.floor(clamped / 60);
  const minutes = clamped % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const bookableSlot = (timeStart, timeEnd) => ({
  time_start: timeStart,
  time_end: timeEnd,
  reservation_end: '',
  closed: false,
});

const spilloverSlot = (timeStart, timeEnd) => ({
  time_start: timeStart,
  time_end: timeEnd,
  reservation_end: timeStart,
  closed: false,
});

const closedSlot = (timeStart, timeEnd) => ({
  time_start: timeStart,
  time_end: timeEnd,
  reservation_end: '',
  closed: true,
});

const closedAllDay = () => closedSlot('00:00', '23:59');

const slotHasReservationEndError = slot => {
  if (!slot.reservation_end) return false;
  return (
    slot.reservation_end < slot.time_start ||
    slot.reservation_end > slot.time_end
  );
};

const slotKind = slot => {
  if (slot.closed) return 'closed';
  if (slot.reservation_end && slot.reservation_end <= slot.time_start) {
    return 'spillover';
  }
  return 'bookable';
};

const expandSavedSlot = entry => {
  if (entry.closed) {
    return [closedSlot(entry.time_start, entry.time_end)];
  }
  const reservationEnd = entry.reservation_end || '';
  if (
    reservationEnd &&
    reservationEnd > entry.time_start &&
    reservationEnd < entry.time_end
  ) {
    return [
      bookableSlot(entry.time_start, reservationEnd),
      spilloverSlot(reservationEnd, entry.time_end),
    ];
  }
  if (reservationEnd && reservationEnd <= entry.time_start) {
    return [spilloverSlot(entry.time_start, entry.time_end)];
  }
  return [bookableSlot(entry.time_start, entry.time_end)];
};

const collapseForSave = slots => {
  const collapsed = [];
  slots.forEach(slot => {
    const last = collapsed[collapsed.length - 1];
    const kind = slotKind(slot);
    if (
      kind === 'spillover' &&
      last &&
      !last.closed &&
      slot.time_start <= last.time_end
    ) {
      const lastKind = slotKind(last);
      if (lastKind === 'bookable') {
        last.reservation_end = last.time_end;
        last.time_end = slot.time_end;
        return;
      }
      if (
        last.reservation_end &&
        last.reservation_end > last.time_start &&
        last.reservation_end < last.time_end
      ) {
        last.time_end = slot.time_end;
        return;
      }
    }
    collapsed.push({ ...slot });
  });
  return collapsed;
};

const mergeSlots = slots => {
  if (slots.length <= 1) return slots;
  const sorted = [...slots].sort((a, b) =>
    a.time_start < b.time_start ? -1 : 1
  );
  return sorted.reduce((merged, slot) => {
    const last = merged[merged.length - 1];
    if (
      last &&
      slot.time_start <= last.time_end &&
      slotKind(last) === slotKind(slot)
    ) {
      if (last.time_end < slot.time_end) {
        last.time_end = slot.time_end;
        last.reservation_end = slot.reservation_end;
        last.closed = slot.closed;
      }
    } else {
      merged.push({ ...slot });
    }
    return merged;
  }, []);
};

const normalizeDay = slots => {
  const valid = slots
    .map(slot => ({
      ...slot,
      rangeStart: parseHm(slot.time_start),
      rangeEnd: parseHm(slot.time_end),
    }))
    .filter(
      slot =>
        slot.rangeStart != null &&
        slot.rangeEnd != null &&
        slot.rangeEnd > slot.rangeStart
    )
    .sort((a, b) => a.rangeStart - b.rangeStart);

  const clipped = [];
  valid.forEach(slot => {
    const prev = clipped[clipped.length - 1];
    if (prev && slot.rangeStart < prev.rangeEnd) {
      slot.rangeStart = prev.rangeEnd;
      slot.time_start = formatHm(slot.rangeStart);
      if (
        !slot.closed &&
        slot.reservation_end &&
        slot.reservation_end <= slot.time_start
      ) {
        slot.reservation_end = slot.time_start;
      }
    }
    if (slot.rangeEnd <= slot.rangeStart) return;
    clipped.push(slot);
  });

  const filled = [];
  let cursor = 0;
  clipped.forEach(slot => {
    if (slot.rangeStart > cursor) {
      filled.push(closedSlot(formatHm(cursor), formatHm(slot.rangeStart)));
    }
    filled.push({
      time_start: slot.time_start,
      time_end: slot.time_end,
      reservation_end: slot.reservation_end || '',
      closed: Boolean(slot.closed),
    });
    cursor = Math.max(cursor, slot.rangeEnd);
  });
  if (cursor < DAY_MINUTES) {
    filled.push(closedSlot(formatHm(cursor), formatHm(DAY_MINUTES)));
  }
  if (filled.length === 0) return [closedAllDay()];
  return mergeSlots(filled);
};

const splitClosedAt = (slots, timeStart, timeEnd) => {
  const start = parseHm(timeStart);
  const requestedEnd = parseHm(timeEnd);
  if (start == null) return normalizeDay(slots);

  const covering = slots.find(slot => {
    if (!slot.closed) return false;
    const coverStart = parseHm(slot.time_start);
    const coverEnd = parseHm(slot.time_end);
    return (
      coverStart != null &&
      coverEnd != null &&
      coverStart <= start &&
      start < coverEnd
    );
  });
  if (!covering) return normalizeDay(slots);

  const coverStart = parseHm(covering.time_start);
  const coverEnd = parseHm(covering.time_end);
  const end = Math.min(
    requestedEnd != null ? requestedEnd : start + DEFAULT_SLOT_DURATION,
    coverEnd
  );
  if (end - start < MIN_DURATION) return normalizeDay(slots);

  const next = [];
  slots.forEach(slot => {
    if (slot !== covering) {
      next.push(slot);
      return;
    }
    if (start > coverStart) {
      next.push(closedSlot(formatHm(coverStart), formatHm(start)));
    }
    next.push(bookableSlot(formatHm(start), formatHm(end)));
    if (end < coverEnd) {
      next.push(closedSlot(formatHm(end), formatHm(coverEnd)));
    }
  });
  return normalizeDay(next);
};

const { t } = useI18n();

// Build per-day state from the schedule.days array.
// Each day is fully covered 00:00–23:59; gaps are closed.
const buildDaySlots = () => {
  const days = props.schedule?.days || [];
  const slots = {};
  ALL_DAYS.forEach(day => {
    const entries = days.filter(d => d.day === day);
    slots[day] = {
      slots: normalizeDay(entries.flatMap(expandSavedSlot)),
    };
  });
  return slots;
};

const localActive = ref(props.active);
const daySlots = ref(buildDaySlots());
const localTimezone = ref(props.timezone);
const localScheduleEnabled = ref(props.workingHoursEnabled);

watch(
  () => props.schedule,
  () => {
    daySlots.value = buildDaySlots();
  },
  { deep: true }
);

watch(
  () => props.active,
  val => {
    localActive.value = val;
  }
);

watch(
  () => props.workingHoursEnabled,
  val => {
    localScheduleEnabled.value = val;
  }
);

const hasTimeError = computed(() =>
  ALL_DAYS.some(day =>
    daySlots.value[day].slots.some(
      slot =>
        !slot.closed &&
        (slot.time_start >= slot.time_end || slotHasReservationEndError(slot))
    )
  )
);

const findSplitInClosed = slots => {
  const tryAt = start => {
    const covering = slots.find(slot => {
      if (!slot.closed) return false;
      const coverStart = parseHm(slot.time_start);
      const coverEnd = parseHm(slot.time_end);
      return (
        coverStart != null &&
        coverEnd != null &&
        coverStart <= start &&
        start < coverEnd &&
        coverEnd - start >= MIN_DURATION
      );
    });
    if (!covering) return null;
    const coverEnd = parseHm(covering.time_end);
    const end = Math.min(start + DEFAULT_SLOT_DURATION, coverEnd);
    if (end - start < MIN_DURATION) return null;
    return { time_start: formatHm(start), time_end: formatHm(end) };
  };

  const preferred = tryAt(PREFERRED_SLOT_START);
  if (preferred) return preferred;
  for (
    let start = 0;
    start <= DAY_MINUTES - MIN_DURATION;
    start += SNAP_MINUTES
  ) {
    const fitted = tryAt(start);
    if (fitted) return fitted;
  }
  return null;
};

const normalizeDaySlots = day => {
  daySlots.value[day].slots = normalizeDay(daySlots.value[day].slots);
};

const addSlot = day => {
  const next = findSplitInClosed(daySlots.value[day].slots);
  if (!next) return;
  daySlots.value[day].slots = splitClosedAt(
    daySlots.value[day].slots,
    next.time_start,
    next.time_end
  );
};

const addSlotAt = (day, slot) => {
  daySlots.value[day].slots = splitClosedAt(
    daySlots.value[day].slots,
    slot.time_start,
    slot.time_end
  );
};

const removeSlot = (day, index) => {
  const slots = daySlots.value[day].slots;
  const removed = slots[index];
  if (removed?.closed) {
    const removedStart = parseHm(removed.time_start);
    const prev = slots.find(
      (slot, slotIndex) =>
        slotIndex !== index && parseHm(slot.time_end) === removedStart
    );
    if (prev) prev.time_end = removed.time_end;
  }
  slots.splice(index, 1);
  daySlots.value[day].slots = normalizeDay(slots);
};

const tzOptions = timeZoneOptions();

const saveSchedule = () => {
  const days = localScheduleEnabled.value
    ? ALL_DAYS.flatMap(day => {
        const tiled = normalizeDay(daySlots.value[day].slots);
        daySlots.value[day].slots = tiled;
        if (tiled.every(slot => slot.closed)) return [];
        return collapseForSave(tiled).map(s => {
          const entry = {
            day,
            time_start: s.time_start,
            time_end: s.time_end,
          };
          if (s.closed) {
            entry.closed = true;
          } else if (s.reservation_end) {
            entry.reservation_end = s.reservation_end;
          }
          return entry;
        });
      })
    : props.schedule?.days || [];

  emit('updateSchedule', {
    working_hours_enabled: localScheduleEnabled.value,
    schedule: {
      days,
      closed_periods: props.schedule?.closed_periods || [],
    },
    timezone: localTimezone.value,
  });
};
</script>

<template>
  <div class="space-y-6 pt-4">
    <!-- Active Toggle -->
    <div class="flex items-center justify-between">
      <div>
        <p class="text-sm font-medium text-n-slate-12">
          {{ t('SETTINGS.SPACES.ACTIVE') }}
        </p>
      </div>
      <Switch
        v-model="localActive"
        @change="emit('updateAvailability', 'active', localActive)"
      />
    </div>

    <!-- Schedule Enabled Toggle -->
    <div class="flex items-center justify-between">
      <div>
        <p class="text-sm font-medium text-n-slate-12">
          {{ t('SETTINGS.SPACES.SCHEDULE.ENABLED') }}
        </p>
        <p v-if="!localScheduleEnabled" class="text-xs text-n-slate-10 mt-0.5">
          {{ t('SETTINGS.SPACES.SCHEDULE.ALWAYS_OPEN_DESC') }}
        </p>
      </div>
      <Switch v-model="localScheduleEnabled" />
    </div>

    <!-- Timezone -->
    <div>
      <p class="text-sm font-medium text-n-slate-12 mb-2">
        {{ t('SETTINGS.SPACES.SCHEDULE.TIMEZONE') }}
      </p>
      <ComboBox
        v-model="localTimezone"
        :options="tzOptions"
        :search-placeholder="t('SETTINGS.SPACES.SCHEDULE.TIMEZONE')"
      />
    </div>

    <!-- Per-Day Schedule -->
    <template v-if="localScheduleEnabled">
      <div>
        <p class="text-sm font-medium text-n-slate-12 mb-1">
          {{ t('SETTINGS.SPACES.SCHEDULE.OPEN_HOURS') }}
        </p>
        <p class="text-xs text-n-slate-10 mb-3">
          {{ t('SETTINGS.SPACES.SCHEDULE.LAST_BOOKABLE_TIME_HINT') }}
        </p>
        <div class="overflow-x-auto">
          <div
            class="space-y-1"
            :style="{
              minWidth: `${9 * 16 + TRACK_WIDTH_PX + HOUR_WIDTH_PX}px`,
            }"
          >
            <div class="flex gap-3 pb-1">
              <div
                class="w-36 shrink-0 sticky left-0 z-20 bg-white dark:bg-slate-900"
              />
              <div class="flex" :style="{ width: `${TRACK_WIDTH_PX}px` }">
                <span
                  v-for="label in HOUR_LABELS"
                  :key="label"
                  class="shrink-0 text-[10px] text-n-slate-10 font-medium"
                  :style="{ width: `${HOUR_WIDTH_PX}px` }"
                >
                  {{ label }}
                </span>
              </div>
            </div>
            <div
              v-for="day in ALL_DAYS"
              :key="day"
              class="flex gap-3 py-2 border-b border-slate-100 dark:border-slate-900 last:border-0"
            >
              <div
                class="w-36 shrink-0 pt-3 sticky left-0 z-20 bg-white dark:bg-slate-900"
              >
                <span class="text-sm font-medium">
                  {{ t(DAY_I18N_KEYS[day]) }}
                </span>
                <button
                  type="button"
                  class="mt-1 flex items-center gap-1 p-0 text-left text-xs text-n-slate-10 hover:text-n-slate-12 transition-colors"
                  @click="addSlot(day)"
                >
                  <Icon icon="i-lucide-plus" class="size-3.5" />
                  {{ t('SETTINGS.SPACES.SCHEDULE.ADD_SLOT') }}
                </button>
              </div>

              <div class="relative z-0 min-w-0 isolate">
                <OpenHoursTimeline
                  :slots="daySlots[day].slots"
                  @remove="index => removeSlot(day, index)"
                  @add-slot="slot => addSlotAt(day, slot)"
                  @normalize="() => normalizeDaySlots(day)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Save Button -->
    <div class="flex justify-end pt-2">
      <Button
        :label="t('SETTINGS.SPACES.SCHEDULE.SAVE')"
        :loading="saving"
        :disabled="hasTimeError"
        @click="saveSchedule"
      />
    </div>
  </div>
</template>
